import { EmailProviderType } from '../enums/email-provider.enum';

/**
 * Maximum total RAW attachment bytes per outbound message, by connected-
 * mailbox provider. Shared by the composer (blocks the upload before any
 * bandwidth is spent) and crm-api (rejects the send with a 400 before
 * calling the provider), so the two can never disagree.
 *
 * Derivations — these are provider API constraints, not product choices:
 *
 * O365 (Microsoft Graph `sendMail`, JSON `fileAttachment.contentBytes`):
 *   Graph v1.0 caps the whole request at 4 MiB and documents ~3 MB of
 *   attachments per single-POST send (larger requires upload sessions,
 *   which we don't implement). Attachment bytes ride base64 (×4/3), and
 *   the HTML body/JSON envelope share the same 4 MiB. Budget:
 *   (4 MiB − 512 KiB headroom) × 3/4 = 2.625 MiB → rounded down to 2.5 MiB.
 *
 * GMAIL (`/upload/gmail/v1/users/me/messages/send?uploadType=media`,
 * raw RFC 822):
 *   The upload endpoint accepts up to ~35 MiB, but Gmail enforces its
 *   classic 25 MiB limit on the encoded MIME message. Attachment bytes
 *   ride base64 with CRLF line wrapping (×~1.37). Budget:
 *   (25 MiB − 1 MiB headroom) × 1/1.37 ≈ 17.5 MiB → rounded down to 17 MiB.
 *   NOTE: this assumes the upload endpoint — Gmail's plain JSON
 *   `messages.send` caps the payload at ~1 MiB and is NOT used for sends
 *   with attachments.
 */
export const EMAIL_PROVIDER_MAX_TOTAL_ATTACHMENT_BYTES: Partial<
	Record<EmailProviderType, number>
> = {
	[EmailProviderType.O365]: 2.5 * 1024 * 1024,
	[EmailProviderType.GMAIL]: 17 * 1024 * 1024,
};

/**
 * Fallback for providers without an entry above — the strictest known
 * limit, so an unknown provider can never over-promise.
 */
export const DEFAULT_MAX_TOTAL_ATTACHMENT_BYTES = 2.5 * 1024 * 1024;

export const getMaxTotalAttachmentBytes = (
	provider: string | null | undefined,
): number => {
	if (provider == null) {
		return DEFAULT_MAX_TOTAL_ATTACHMENT_BYTES;
	}

	return (
		EMAIL_PROVIDER_MAX_TOTAL_ATTACHMENT_BYTES[provider as EmailProviderType] ??
		DEFAULT_MAX_TOTAL_ATTACHMENT_BYTES
	);
};
